/**
 * Zustand session store — follows andavo-dev's "separated-creator" pattern
 * (see libs/client/shared/shared/util/api/src/session/userStore.ts): a typed
 * StateCreator passed into create(), wrapped with devtools. Non-React code can
 * read `.getState()`; React components use the hook.
 *
 * Holds ephemeral chat-session UI state: the active conversation id, the
 * in-flight (not-yet-persisted) message list, the files staged in the composer,
 * and any RAG chunks surfaced for the current turn. Server data (the
 * conversation list, a loaded conversation) lives in TanStack Query, NOT here —
 * this store is only client-owned UI state, matching the andavo-dev split.
 *
 * When the user OPENS a saved conversation, the loaded messages are pushed into
 * `draftMessages` via `loadDraft` so the thread + composer keep operating off
 * the single working list (mirrors chat.js's `conversationHistory`).
 */
import type { StateCreator } from 'zustand';
import { create } from 'zustand';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';
import type { Citation, RagChunk } from '@/features/chat/api-types';
import type { PendingAttachment, SessionMessage } from '@/features/chat/types';

/**
 * Artifacts side-panel UI state. `open` + `messageIndex` are EPHEMERAL (which
 * message's artifact is currently lifted into the panel — meaningless across a
 * reload, since the conversation/draft may differ), while `width` is the user's
 * durable preference and is the ONLY field persisted to localStorage.
 */
export type ArtifactPanelState = {
  /** Whether the panel is currently shown. */
  open: boolean;
  /** Panel width in px, clamped to [MIN, MAX]. The persisted preference. */
  width: number;
  /** Index (into draftMessages) of the message whose artifact is shown. */
  messageIndex: number | null;
};

/** Resize clamp — narrow enough to keep the transcript usable, wide enough to read. */
export const ARTIFACT_MIN_WIDTH = 320;
export const ARTIFACT_MAX_WIDTH = 720;
export const ARTIFACT_DEFAULT_WIDTH = 420;

/** Clamp a requested panel width into the allowed range. */
export function clampArtifactWidth(px: number): number {
  if (Number.isNaN(px)) return ARTIFACT_DEFAULT_WIDTH;
  return Math.min(
    ARTIFACT_MAX_WIDTH,
    Math.max(ARTIFACT_MIN_WIDTH, Math.round(px)),
  );
}

/**
 * A localStorage that degrades to a no-op when the real one is unusable —
 * Safari private mode throws on write, SSR has no `window`, and some jsdom
 * configurations expose a `localStorage` whose methods aren't callable. Persist
 * is a nice-to-have (it only remembers the panel width), so a missing/throwing
 * store must never break the app or the test suite; it just stops persisting.
 */
function safeLocalStorage(): Storage | undefined {
  try {
    const ls = globalThis.localStorage;
    if (ls && typeof ls.setItem === 'function') {
      // Probe: private mode often exposes the API but throws on write.
      const probe = '__experts_persist_probe__';
      ls.setItem(probe, '1');
      ls.removeItem(probe);
      return ls;
    }
  } catch {
    /* unavailable — fall through to no-op */
  }
  return undefined;
}

/** A do-nothing Storage used when localStorage is unusable (persist becomes a no-op). */
const noopStorage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> = {
  getItem: () => null,
  setItem: () => {},
  removeItem: () => {},
};

type SessionState = {
  /** The conversation currently open, or null for a fresh/unsaved chat. */
  activeConversationId: string | null;
  /** The working message list shown in the composer (optimistic + streamed). */
  draftMessages: SessionMessage[];
  /** True while an assistant response is streaming. */
  isStreaming: boolean;
  /** Files staged in the composer for the next turn (with upload progress). */
  pendingAttachments: PendingAttachment[];
  /** RAG chunks surfaced by the backend for the latest turn (bench-trace path). */
  ragChunks: RagChunk[];
  /** Artifacts side-panel UI state (open/width/which message). */
  artifactPanel: ArtifactPanelState;
  /**
   * How many times openArtifact() has been called. Monotonic; never reset. It
   * lets the page tell "the artifact was opened AFTER the conversations sheet"
   * apart from "it was already open" without an effect (see narrowSurface.ts).
   */
  artifactOpenSeq: number;

  /**
   * Bumped whenever the working draft is SWITCHED to another conversation
   * (open, new, delete-active): setActiveConversation to a different id,
   * loadDraft, reset. An in-flight turn captures it at start and abandons
   * itself if it changes, so its late frames never land in — and it never
   * saves — a conversation the user moved to mid-stream.
   */
  draftEpoch: number;
  setActiveConversation: (id: string | null) => void;
  /**
   * Record the id the server just assigned to the CURRENT draft (first save
   * of a new conversation). Not a navigation, so the draft epoch is unchanged
   * — otherwise it would cancel an open the user started meanwhile.
   */
  adoptConversationId: (id: string) => void;
  /** Replace the whole draft list (used when opening a saved conversation). */
  loadDraft: (messages: SessionMessage[]) => void;
  appendMessage: (message: SessionMessage) => void;
  /** Replace the content of the last assistant message (streaming append). */
  appendToLastAssistant: (delta: string) => void;
  /**
   * Attach inline source citations (+ the no-authoritative-source flag) to the
   * last assistant message. Called when the final SSE `citations` metadata frame
   * arrives (just before [DONE]). No-op if the last message is not an assistant
   * turn. Idempotent-ish: replaces whatever citations were set.
   */
  setLastAssistantCitations: (
    citations: Citation[],
    hasUnsourcedClaim: boolean,
  ) => void;
  /**
   * Remove the trailing assistant message if there is one (used by Regenerate:
   * drop the previous reply before re-streaming a fresh one). No-op if the last
   * message is not an assistant turn.
   */
  dropLastAssistant: () => void;
  /**
   * Remove the trailing assistant message ONLY if it is empty (used after a Stop
   * before any token arrived — don't persist a blank assistant bubble that would
   * render as "…"). No-op otherwise.
   */
  dropTrailingEmptyAssistant: () => void;
  setStreaming: (streaming: boolean) => void;
  setRagChunks: (chunks: RagChunk[]) => void;

  // ── Artifacts side-panel ──
  /** Open the panel showing the artifact for the given message index. */
  openArtifact: (messageIndex: number) => void;
  /** Close the panel (keeps the width preference; clears the active message). */
  closeArtifact: () => void;
  /** Set the panel width (clamped to [MIN, MAX]). */
  setArtifactWidth: (px: number) => void;

  // ── Composer attachments ──
  addPendingAttachment: (attachment: PendingAttachment) => void;
  updatePendingAttachment: (
    clientId: string,
    patch: Partial<PendingAttachment>,
  ) => void;
  removePendingAttachment: (clientId: string) => void;
  clearPendingAttachments: () => void;

  /** Reset to a fresh, empty chat (the "New conversation" affordance). */
  reset: () => void;
};

/**
 * The reset-able slice. NOTE: artifactPanel.width is intentionally NOT reset here
 * — "New conversation" closes the panel but keeps the user's width preference.
 * `reset()` composes this with a panel-close below.
 */
const INITIAL: Pick<
  SessionState,
  | 'activeConversationId'
  | 'draftMessages'
  | 'isStreaming'
  | 'pendingAttachments'
  | 'ragChunks'
> = {
  activeConversationId: null,
  draftMessages: [],
  isStreaming: false,
  pendingAttachments: [],
  ragChunks: [],
};

const INITIAL_ARTIFACT_PANEL: ArtifactPanelState = {
  open: false,
  width: ARTIFACT_DEFAULT_WIDTH,
  messageIndex: null,
};

const storeCreator: StateCreator<
  SessionState,
  [['zustand/devtools', never], ['zustand/persist', unknown]]
> = (set) => ({
  ...INITIAL,
  artifactPanel: INITIAL_ARTIFACT_PANEL,
  artifactOpenSeq: 0,
  draftEpoch: 0,
  setActiveConversation: (id) =>
    set((s) =>
      s.activeConversationId === id
        ? {}
        : { activeConversationId: id, draftEpoch: s.draftEpoch + 1 },
    ),
  adoptConversationId: (id) => set({ activeConversationId: id }),
  loadDraft: (messages) =>
    // Loading a saved conversation replaces the whole draft, so any open
    // artifact's messageIndex now points into a DIFFERENT message list — close
    // the panel rather than risk showing a stale/mismatched artifact. Width
    // preference is kept. (Per the 2026-06-20 codex review, finding #6.)
    set((s) => ({
      draftMessages: messages,
      draftEpoch: s.draftEpoch + 1,
      ragChunks: [],
      artifactPanel: { ...s.artifactPanel, open: false, messageIndex: null },
    })),
  appendMessage: (message) =>
    set((s) => ({ draftMessages: [...s.draftMessages, message] })),
  appendToLastAssistant: (delta) =>
    set((s) => {
      const next = [...s.draftMessages];
      const last = next[next.length - 1];
      if (last && last.role === 'assistant') {
        next[next.length - 1] = { ...last, content: last.content + delta };
      } else {
        next.push({ role: 'assistant', content: delta });
      }
      return { draftMessages: next };
    }),
  setLastAssistantCitations: (citations, hasUnsourcedClaim) =>
    set((s) => {
      const next = [...s.draftMessages];
      const last = next[next.length - 1];
      if (last && last.role === 'assistant') {
        next[next.length - 1] = { ...last, citations, hasUnsourcedClaim };
        return { draftMessages: next };
      }
      return {};
    }),
  dropLastAssistant: () =>
    set((s) => {
      const last = s.draftMessages[s.draftMessages.length - 1];
      if (last?.role === 'assistant') {
        return { draftMessages: s.draftMessages.slice(0, -1) };
      }
      return {};
    }),
  dropTrailingEmptyAssistant: () =>
    set((s) => {
      const last = s.draftMessages[s.draftMessages.length - 1];
      if (last?.role === 'assistant' && last.content === '') {
        return { draftMessages: s.draftMessages.slice(0, -1) };
      }
      return {};
    }),
  setStreaming: (streaming) => set({ isStreaming: streaming }),
  setRagChunks: (chunks) => set({ ragChunks: chunks }),

  openArtifact: (messageIndex) =>
    set((s) => ({
      artifactPanel: { ...s.artifactPanel, open: true, messageIndex },
      artifactOpenSeq: s.artifactOpenSeq + 1,
    })),
  closeArtifact: () =>
    set((s) => ({
      artifactPanel: { ...s.artifactPanel, open: false, messageIndex: null },
    })),
  setArtifactWidth: (px) =>
    set((s) => ({
      artifactPanel: { ...s.artifactPanel, width: clampArtifactWidth(px) },
    })),

  addPendingAttachment: (attachment) =>
    set((s) => ({
      pendingAttachments: [...s.pendingAttachments, attachment],
    })),
  updatePendingAttachment: (clientId, patch) =>
    set((s) => ({
      pendingAttachments: s.pendingAttachments.map((a) =>
        a.clientId === clientId ? { ...a, ...patch } : a,
      ),
    })),
  removePendingAttachment: (clientId) =>
    set((s) => ({
      pendingAttachments: s.pendingAttachments.filter(
        (a) => a.clientId !== clientId,
      ),
    })),
  clearPendingAttachments: () => set({ pendingAttachments: [] }),

  reset: () =>
    // New conversation: clear the working chat AND close the artifact panel,
    // but KEEP the user's width preference (don't reset a durable UI choice).
    set((s) => ({
      ...INITIAL,
      draftEpoch: s.draftEpoch + 1,
      artifactPanel: {
        width: s.artifactPanel.width,
        open: false,
        messageIndex: null,
      },
    })),
});

export const useSessionStore = create<SessionState>()(
  devtools(
    persist(storeCreator, {
      name: 'experts-session',
      // Guarded storage: no-ops cleanly where localStorage is unusable (Safari
      // private mode, SSR, some jsdom setups) instead of throwing.
      storage: createJSONStorage(() => safeLocalStorage() ?? noopStorage),
      // Persist ONLY the durable panel width preference. open/messageIndex are
      // ephemeral (a reopened-on-reload panel would point at a stale message),
      // and the chat draft/streaming state is owned per-session, not persisted.
      partialize: (state) => ({
        artifactPanel: { width: state.artifactPanel.width },
      }),
      // Merge the persisted width onto a fresh INITIAL_ARTIFACT_PANEL so a
      // partial persisted slice never leaks open=true or a stale messageIndex.
      merge: (persisted, current) => {
        const p = persisted as
          | { artifactPanel?: Partial<ArtifactPanelState> }
          | undefined;
        return {
          ...current,
          artifactPanel: {
            ...INITIAL_ARTIFACT_PANEL,
            width: clampArtifactWidth(
              p?.artifactPanel?.width ?? ARTIFACT_DEFAULT_WIDTH,
            ),
          },
        };
      },
    }),
    { name: 'experts-session' },
  ),
);
